/**
 * Dev Impersonation Panel — Unified acceptance testing rig.
 *
 * Single floating panel (replaces 3 old panels: ActorSwitch, TenantSwitch, IndustrySwitch).
 * Actor-first cascade: pick an actor → tenant, view layer, and industry branding auto-set.
 *
 * Two states:
 *   Minimized: pill badge bottom-right showing persona + role color
 *   Expanded: panel with actor selector, tenant info, view layer, observer toggle
 *
 * Keyboard: Ctrl+D toggles expanded/minimized
 *
 * Cascade rules:
 *   Same tenant → no reload (dispatch ui:token-set, update view layer + industry in-place)
 *   Different tenant → reload (Apollo cache is tenant-scoped)
 *
 * Dev mode only — returns null if ENABLE_DEV_MODE !== 'true'.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Select,
  ActionIcon,
  Collapse,
  Tooltip,
  Switch,
  Divider,
  ThemeIcon,
} from '@mantine/core';
import {
  IconUser,
  IconChevronDown,
  IconChevronUp,
  IconX,
  IconBuilding,
  IconEye,
  IconEyeOff,
} from '@tabler/icons-react';

import { gql } from '@apollo/client';
// eslint-disable-next-line no-restricted-imports -- Dev tooling requires direct Apollo access
import { useQuery } from '@apollo/client/react';

import { isDevModeEnabled, getConfiguredTenant } from '@/utils/env';
import { useAuth, useViewLayer } from '@/auth/AuthContext';
import { apolloClient, setObserverMode, isObserverModeActive } from '@/utils/apollo-client';

// ── Backend scope resolution (DRR-103) ──────────────────────────────────────
//
// Fetches merged scopes from role configs via GraphQL.
// Config is the single source of truth — no hardcoded scope maps in frontend.

const RESOLVE_ROLE_SCOPES = gql`
  query ResolveRoleScopes($roleIds: [String!]!) {
    resolveRoleScopes(roleIds: $roleIds)
  }
`;

// ── Fetch actors from backend (DRR-303: Remove hardcoded actors) ────────────
const LIST_ACTORS = gql`
  query ListActorsForDevPanel($limit: Int) {
    listActors(limit: $limit) {
      items {
        id
        name
        role
        roleIds
        actorType
        category
      }
    }
  }
`;

interface BackendActor {
  id: string;
  name: string;
  role: string | null;
  roleIds: string[];
  actorType: string;
  category: string | null;
}

/**
 * Fetch scopes for role IDs from the backend.
 * DRR-102: ALL roles resolve via backend role configs — no frontend short-circuits.
 * Returns empty array on failure (graceful degradation — no access, not full access).
 */
async function fetchRoleScopes(roleIds: string[]): Promise<string[]> {
  if (roleIds.length === 0) return [];

  try {
    const { data } = await apolloClient.query<{ resolveRoleScopes: string[] }>({
      query: RESOLVE_ROLE_SCOPES,
      variables: { roleIds },
      fetchPolicy: 'network-only',
    });
    return data?.resolveRoleScopes ?? [];
  } catch (err) {
    console.warn('[DevPanel] Failed to resolve role scopes from backend, falling back to empty:', err);
    return [];
  }
}

// ── Actor + Tenant definitions ──────────────────────────────────────────────

interface DevActor {
  id: string;
  name: string;
  role: string;
  /** Role config IDs (e.g., ["rn-alf"]). Backend resolves these to scopes. */
  roleIds: string[];
  defaultTenant: string;
  group: string;
}

/**
 * Convert backend Actor to DevActor format.
 * Tenant is derived from actor ID prefix (e.g., "2_1" → tenant 2 → "hillside-harmony")
 */
function toDevActor(actor: BackendActor, tenantSlug: string, tenantName: string): DevActor {
  return {
    id: actor.id,
    name: actor.name,
    role: actor.role ?? actor.category ?? 'UNKNOWN',
    roleIds: actor.roleIds ?? [],
    defaultTenant: tenantSlug,
    group: tenantName,
  };
}

/**
 * Derive view layer from resolved scopes (DRR-102).
 *
 * Replaces the old hardcoded ROLE_TO_VIEW_LAYER map that was based on role display
 * names and incorrectly elevated staff (RN, LPN, Billing, QA) to 'admin' view.
 *
 * Rules:
 *   - architect: has workflow:admin OR tenant:admin OR admin role-id
 *   - admin:     has staff:manage OR staff:supervise OR facility:admin
 *   - user:      everything else (fail-closed default)
 */
function deriveViewLayerFromScopes(
  scopes: string[],
  roleIds: string[],
): 'architect' | 'admin' | 'user' {
  // Architect: system-level admin or superadmin scopes only (role names avoided — use backend myViewLayer)
  if (roleIds.includes('admin')) return 'architect';
  if (scopes.includes('*')) return 'architect';
  if (scopes.some((s) => s === 'workflow:admin' || s === 'tenant:admin')) return 'architect';

  // Admin: supervisory / management scopes
  if (
    scopes.some(
      (s) =>
        s === 'staff:manage' ||
        s === 'staff:supervise' ||
        s === 'facility:admin' ||
        s.startsWith('payroll:manage') ||
        s.startsWith('accounting:manage'),
    )
  ) {
    return 'admin';
  }

  // Default: user (fail-closed)
  return 'user';
}

const ROLE_COLORS: Record<string, string> = {
  // Executive layer
  CEO: 'violet',
  CFO: 'violet',
  COO: 'violet',
  // Admin layer
  ALD: 'violet',
  DoN: 'red',
  ADMIN: 'violet',
  DON: 'red',
  // Clinical staff
  RN: 'red',
  LPN: 'pink',
  CNA: 'teal',
  'Med Aide': 'lime',
  MED_AIDE: 'lime',
  // Operational staff
  Billing: 'blue',
  QA: 'orange',
  BILLING_CLERK: 'blue',
  QA_SPECIALIST: 'orange',
  // Legacy names (backward compat)
  CLAIMS_REVIEWER: 'blue',
  FINANCIAL_ANALYST: 'green',
  CARE_COORDINATOR: 'pink',
  HOME_CARE_WORKER: 'teal',
  RESIDENT_ASSOCIATE: 'cyan',
  PAYROLL_PROCESSOR: 'green',
  PAYROLL_MANAGER: 'green',
  EMPLOYEE: 'teal',
  ACCOUNTANT: 'grape',
  AUDITOR: 'orange',
  RESIDENT: 'yellow',
  VOLUNTEER: 'indigo',
  // B2C consumer
  FAMILY: 'cyan',
};

const VIEW_LAYER_COLORS: Record<string, string> = {
  architect: 'violet',
  admin: 'orange',
  user: 'blue',
};

const DEFAULT_ACTOR: DevActor = {
  id: '2_1',
  name: 'Phineas Vang',
  role: 'ALD',
  roleIds: ['role-4-0-1'],
  defaultTenant: 'hillside-harmony',
  group: 'Hillside Harmony',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function getCurrentActorId(): string {
  try {
    return localStorage.getItem('dev_actor_auth_id') || '2_1';
  } catch {
    return '2_1';
  }
}

function getCurrentTenantId(): string {
  try {
    // Must match AuthContext's resolution order:
    // localStorage > window.__TENANT__ (getConfiguredTenant) > 'ferp-system'
    return localStorage.getItem('tenant_id') || getConfiguredTenant() || 'ferp-system';
  } catch {
    return 'ferp-system';
  }
}

// ── Component ───────────────────────────────────────────────────────────────

export function DevImpersonationPanel() {
  const [expanded, setExpanded] = useState(false);
  const [showTenantOverride, setShowTenantOverride] = useState(false);
  const [observerActive, setObserverActive] = useState(isObserverModeActive);
  const [currentActorId, setCurrentActorId] = useState(getCurrentActorId);
  const [currentTenantId, setCurrentTenantId] = useState(getCurrentTenantId);

  const { setViewLayer } = useAuth();
  const viewLayer = useViewLayer();

  // Dev mode guard — must be before hooks per rules of hooks
  const isDevMode = isDevModeEnabled();

  // Fetch actors from backend (DRR-303: no hardcoded actors)
  const { data: actorData, loading: actorsLoading } = useQuery<{
    listActors: { items: BackendActor[] };
  }>(LIST_ACTORS, {
    variables: { limit: 200 },
    skip: !isDevMode,
    fetchPolicy: 'cache-first',
  });

  // Transform backend actors to DevActor format
  // Tenant mapping: actor ID prefix → tenant slug
  const allActors = useMemo((): DevActor[] => {
    if (!actorData?.listActors?.items) return [DEFAULT_ACTOR];

    const tenantMap: Record<string, { slug: string; name: string }> = {
      '0': { slug: 'ferp-system', name: 'FERP System' },
      '1': { slug: 'ferp-operations', name: 'FERP Operations' },
      '2': { slug: 'hillside-harmony', name: 'Hillside Harmony' },
      '3': { slug: 'athanor-alf', name: 'Athanor ALF' },
      '4': { slug: 'omniquark', name: 'Omniquark' },
      '5': { slug: 'cc-kitchen', name: 'CC Kitchen' },
      '6': { slug: 'cc-laundry', name: 'CC Laundry' },
      '7': { slug: 'aahhcs', name: 'AAHHCS' },
      '21': { slug: 'athanor-wellness', name: 'Athanor Wellness' },
      '22': { slug: 'athanor-memorial', name: 'Athanor Memorial' },
      '23': { slug: 'phin-personal', name: 'Personal Finance' },
    };

    return actorData.listActors.items
      .filter((a) => a.actorType === 'HUMAN')
      .map((actor) => {
        const tenantNum = actor.id.split('_')[0] ?? '0';
        const tenant = tenantMap[tenantNum] ?? { slug: 'unknown', name: 'Unknown' };
        return toDevActor(actor, tenant.slug, tenant.name);
      });
  }, [actorData]);

  if (!isDevMode) return null;

  const currentActor = useMemo(
    () => allActors.find((a) => a.id === currentActorId) ?? DEFAULT_ACTOR,
    [currentActorId, allActors],
  );

  const roleColor = ROLE_COLORS[currentActor.role] ?? 'gray';

  // Filter actors by current tenant
  const tenantActors = useMemo(
    () => allActors.filter((a) => a.defaultTenant === currentTenantId),
    [currentTenantId, allActors],
  );

  // Grouped select data for actors (filtered by current tenant)
  const actorSelectData = useMemo(() => {
    const groups = new Map<string, { value: string; label: string }[]>();
    for (const actor of tenantActors) {
      const items = groups.get(actor.group) ?? [];
      items.push({ value: actor.id, label: `${actor.name} (${actor.role})` });
      groups.set(actor.group, items);
    }
    return Array.from(groups.entries()).map(([group, items]) => ({ group, items }));
  }, [tenantActors]);

  // Bootstrap scopes on mount — always re-fetch to pick up backend role changes.
  // handleActorSelect only fires on CHANGE — on initial load or page reload,
  // dev_actor_scopes may be stale. Always fetch fresh scopes so any backend
  // role config update (e.g., adding * to admin) takes effect after reload.
  useEffect(() => {
    if (currentActor.roleIds.length > 0) {
      // Ensure role IDs are in localStorage for x-ferp-role-ids header
      localStorage.setItem('dev_actor_role_ids', currentActor.roleIds.join(','));
      localStorage.setItem('dev_actor_auth_id', currentActor.id);

      fetchRoleScopes(currentActor.roleIds).then((scopes) => {
        if (scopes.length > 0) {
          localStorage.setItem('dev_actor_scopes', scopes.join(','));
          const derivedLayer = deriveViewLayerFromScopes(scopes, currentActor.roleIds);
          setViewLayer(derivedLayer);
        }
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fix actor/tenant mismatch on page load
  // If current actor doesn't belong to current tenant, auto-select appropriate actor
  useEffect(() => {
    if (currentActor.defaultTenant !== currentTenantId) {
      const newTenantActors = allActors.filter((a) => a.defaultTenant === currentTenantId);
      const adminActor = newTenantActors.find((a) =>
        a.role === 'ALD' || a.role === 'ADMIN' || a.roleIds.includes('admin')
      );
      const defaultActor = adminActor ?? newTenantActors[0];

      if (defaultActor) {
        localStorage.setItem('dev_actor_auth_id', defaultActor.id);
        localStorage.setItem('dev_actor_role_ids', defaultActor.roleIds.join(','));
        setCurrentActorId(defaultActor.id);

        // Fetch scopes for the new actor
        fetchRoleScopes(defaultActor.roleIds).then((scopes) => {
          if (scopes.length > 0) {
            localStorage.setItem('dev_actor_scopes', scopes.join(','));
            const derivedLayer = deriveViewLayerFromScopes(scopes, defaultActor.roleIds);
            setViewLayer(derivedLayer);
          }
          // Refresh Apollo cache with new actor context
          apolloClient.resetStore().catch(() => {});
        });
      }
    }
  }, [currentTenantId, allActors]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard shortcut: Ctrl+D
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'd') {
        e.preventDefault();
        setExpanded((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ── Cascade: Actor → Tenant → View Layer → Industry ──
  //
  // Two modes:
  //   Mock auth (ENABLE_MOCK_AUTH=true): checkSession reads DEV_ACTOR_MAP,
  //     so ui:token-set dispatch re-runs mock auth with new actor. Same-tenant
  //     switches can be in-place.
  //   OIDC auth (ENABLE_MOCK_AUTH=false): checkSession reads JWT, ignores
  //     DEV_ACTOR_MAP. Actor selection only affects Apollo x-ferp-user-id header.
  //     Don't dispatch ui:token-set (it overwrites viewLayer from JWT).

  const handleActorSelect = useCallback(
    (actorId: string | null) => {
      if (!actorId || actorId === currentActorId) return;

      const actor = allActors.find((a) => a.id === actorId);
      if (!actor) return;

      const previousTenant = currentTenantId;
      const newTenant = actor.defaultTenant;

      // 1. Write actor ID + role IDs immediately (Apollo authLink reads per-request)
      localStorage.setItem('dev_actor_auth_id', actorId);
      localStorage.setItem('dev_actor_role_ids', actor.roleIds.join(','));

      // 2. Fetch scopes from backend (config is single source of truth)
      //    Scopes drive BOTH permission filtering AND view layer derivation (DRR-102).
      //    IMPORTANT: Do NOT dispatch ui:token-set until scopes are written.
      //    Early dispatch causes Apollo to refetch allowedNavigation with STALE scopes
      //    ("one persona behind" bug).
      fetchRoleScopes(actor.roleIds).then((scopes) => {
        if (scopes.length > 0) {
          localStorage.setItem('dev_actor_scopes', scopes.join(','));
        } else {
          localStorage.removeItem('dev_actor_scopes');
        }

        // 3. Derive view layer from actual scopes (not hardcoded role names)
        const derivedLayer = deriveViewLayerFromScopes(scopes, actor.roleIds);
        setViewLayer(derivedLayer);

        // 4. Evict Apollo cache so allowedNavigation re-fetches with NEW scopes.
        // resetStore() clears cache AND refetches all active queries.
        apolloClient.resetStore().catch(() => { /* ignore — queries will refetch anyway */ });

        // 5. Dispatch AFTER scopes + view layer are set so AuthContext picks up everything at once.
        if (newTenant === previousTenant) {
          window.dispatchEvent(new Event('ui:token-set'));
        }
      });

      if (newTenant !== previousTenant) {
        // Different tenant → must reload (Apollo cache is tenant-scoped)
        localStorage.setItem('tenant_id', newTenant);
        window.location.reload();
        return;
      }

      // Same tenant → update in-place
      setCurrentActorId(actorId);
    },
    [currentActorId, currentTenantId, setViewLayer, allActors],
  );

  // Tenant override — auto-select first actor from new tenant
  const handleTenantOverride = useCallback(
    (tenantId: string | null) => {
      if (!tenantId || tenantId === currentTenantId) return;

      // Find first actor from the new tenant (prefer admin roles)
      const newTenantActors = allActors.filter((a) => a.defaultTenant === tenantId);
      const adminActor = newTenantActors.find((a) =>
        a.role === 'ALD' || a.role === 'ADMIN' || a.roleIds.includes('admin')
      );
      const defaultActor = adminActor ?? newTenantActors[0];

      if (defaultActor) {
        localStorage.setItem('dev_actor_auth_id', defaultActor.id);
        localStorage.setItem('dev_actor_role_ids', defaultActor.roleIds.join(','));
      }

      localStorage.setItem('tenant_id', tenantId);
      window.location.reload();
    },
    [currentTenantId, allActors],
  );

  // Observer mode
  const handleObserverToggle = useCallback((checked: boolean) => {
    setObserverMode(checked);
    setObserverActive(checked);
  }, []);

  // ── Minimized badge ───────────────────────────────────────────────────────

  if (!expanded) {
    return (
      <Tooltip label="Dev Persona (Ctrl+D)">
        <Paper
          shadow="md"
          radius="xl"
          data-testid="dev-persona-pill"
          style={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1100,
            cursor: 'pointer',
            padding: '6px 14px',
          }}
          onClick={() => setExpanded(true)}
        >
          <Group gap="xs">
            <ThemeIcon size="xs" radius="xl" color={VIEW_LAYER_COLORS[viewLayer] ?? 'gray'} variant="filled">
              <IconUser size={10} />
            </ThemeIcon>
            <Text size="xs" fw={600}>{actorsLoading ? 'Loading...' : currentActor.name}</Text>
            <Badge size="xs" color={roleColor} variant="light">{currentActor.role}</Badge>
            {observerActive && <Badge size="xs" color="teal" variant="filled">OBS</Badge>}
          </Group>
        </Paper>
      </Tooltip>
    );
  }

  // ── Expanded panel ────────────────────────────────────────────────────────

  return (
    <Paper
      shadow="xl"
      radius="md"
      p="md"
      withBorder
      data-testid="dev-persona-panel"
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        width: 380,
        zIndex: 1100,
      }}
    >
      <Stack gap="sm">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon size="sm" color={VIEW_LAYER_COLORS[viewLayer] ?? 'gray'} variant="light">
              <IconUser size={14} />
            </ThemeIcon>
            <Text size="sm" fw={600}>Dev Persona</Text>
            <Text size="xs" c="dimmed">(Ctrl+D)</Text>
          </Group>
          <Group gap={4}>
            <ActionIcon variant="subtle" size="sm" onClick={() => setExpanded(false)}>
              <IconX size={14} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Actor Selector (primary) */}
        <Select
          size="xs"
          label="Actor"
          value={currentActorId}
          onChange={handleActorSelect}
          data={actorSelectData}
          allowDeselect={false}
          searchable
          disabled={actorsLoading}
          placeholder={actorsLoading ? 'Loading actors...' : undefined}
          comboboxProps={{ zIndex: 1200 }}
          data-testid="dev-actor-select"
        />

        {/* Current actor details */}
        <Group gap="xs">
          <Badge size="xs" color={roleColor} variant="filled">{currentActor.role}</Badge>
          <Text size="xs" c="dimmed">at</Text>
          <Badge size="xs" variant="outline">{currentTenantId}</Badge>
        </Group>

        <Divider />

        {/* Tenant (auto-set, with override toggle) */}
        <Group justify="space-between">
          <Group gap="xs">
            <IconBuilding size={14} />
            <Text size="xs" fw={500}>Tenant</Text>
            <Text size="xs" c="dimmed">{currentTenantId}</Text>
          </Group>
          <ActionIcon
            variant="subtle"
            size="xs"
            onClick={() => setShowTenantOverride(!showTenantOverride)}
          >
            {showTenantOverride ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
          </ActionIcon>
        </Group>

        <Collapse in={showTenantOverride}>
          <Select
            size="xs"
            placeholder="Override tenant..."
            value={currentTenantId}
            onChange={handleTenantOverride}
            data={[
              { group: 'System', items: [
                { value: 'ferp-system', label: 'FERP System (#0)' },
                { value: 'ferp-operations', label: 'FERP Operations (#1)' },
              ]},
              { group: 'Healthcare', items: [
                { value: 'hillside-harmony', label: 'Hillside Harmony (#2)' },
                { value: 'athanor-alf', label: 'Athanor ALF (#3)' },
                { value: 'aahhcs', label: 'AAHHCS (#7)' },
              ]},
              { group: 'Services', items: [
                { value: 'cc-kitchen', label: 'CC Kitchen (#5)' },
                { value: 'cc-laundry', label: 'CC Laundry (#6)' },
                { value: 'athanor-wellness', label: 'Athanor Wellness (#21)' },
                { value: 'athanor-memorial', label: 'Athanor Memorial (#22)' },
              ]},
              { group: 'Other', items: [
                { value: 'omniquark', label: 'Omniquark (#4)' },
                { value: 'phin-personal', label: 'Personal Finance (#23)' },
              ]},
            ]}
            searchable
            comboboxProps={{ zIndex: 1200 }}
          />
        </Collapse>

        <Divider />

        {/* View Layer (auto-derived from role scopes — read-only, DRR-102) */}
        <Group gap="xs">
          <Text size="xs" fw={500}>View:</Text>
          <Badge
            size="sm"
            variant="filled"
            color={VIEW_LAYER_COLORS[viewLayer] ?? 'gray'}
          >
            {viewLayer}
          </Badge>
          <Text size="xs" c="dimmed">(role-locked)</Text>
        </Group>

        <Divider />

        {/* Observer Mode */}
        <Group justify="space-between">
          <Group gap="xs">
            {observerActive ? <IconEye size={14} color="teal" /> : <IconEyeOff size={14} />}
            <Text size="xs" fw={500}>Observer Mode</Text>
            <Text size="xs" c="dimmed">PII-free workflow view</Text>
          </Group>
          <Switch
            size="xs"
            checked={observerActive}
            onChange={(e) => handleObserverToggle(e.currentTarget.checked)}
            color="teal"
          />
        </Group>
      </Stack>
    </Paper>
  );
}
